import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Theme } from "@mui/material/styles";

const CONFIRMATION_TEXT = "delete permanently";

interface DeleteGoalStepConfirmationModalProps {
  title: string;
  description: React.ReactNode;
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  isLoading?: boolean;
  theme: Theme;
}

const DeleteGoalStepConfirmationModal: React.FC<
  DeleteGoalStepConfirmationModalProps
> = ({ title, description, open, onCancel, onConfirm, isLoading, theme }) => {
  const [confirmationText, setConfirmationText] = useState("");
  const [error, setError] = useState("");

  const handleConfirm = () => {
    if (!confirmationText.trim()) {
      setError("This field is required");
      return;
    }

    if (confirmationText !== CONFIRMATION_TEXT) {
      setError("Type 'permanently delete' to confirm");
      return;
    }

    setError("");
    onConfirm();
  };

  return (
    <Dialog
      open={open}
      onClose={isLoading ? undefined : onCancel}
      disableEscapeKeyDown={isLoading}
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: 760,
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: theme.palette.primary.main,
          color: theme.palette.surface.light,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
        }}
      >
        <Typography variant="headlineMedium" fontWeight={700}>
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          disabled={isLoading}
          sx={{ color: theme.palette.surface.light }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 4 }}>
        <Typography
          component="div"
          variant="bodyLarge"
          color={theme.palette.textMain.dark2}
          sx={{ mb: 3 }}
        >
          {description}
        </Typography>
        <TextField
          fullWidth
          value={confirmationText}
          onChange={(event) => {
            setConfirmationText(event.target.value);
            if (error) setError("");
          }}
          placeholder={CONFIRMATION_TEXT}
          error={!!error}
          helperText={error}
          disabled={isLoading}
        />
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: "1px solid #ccc",
          px: 3,
          py: 2.5,
          gap: 2,
        }}
      >
        <Box flex={1} />
        <Button variant="outlined" onClick={onCancel} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isLoading}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteGoalStepConfirmationModal;
